import { lazy, Suspense, useEffect, type ComponentType, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from '@/presentation/contexts/AuthContext';
import { MainShell } from '@/presentation/layouts/MainShell';
import { AssistantsPage } from '@/presentation/pages/AssistantsPage';
import { CajaDetailPage } from '@/presentation/pages/CajaDetailPage';
import { OwnershipParticipationPage } from '@/presentation/pages/OwnershipParticipationPage';
import { CommunicationsPage } from '@/presentation/pages/CommunicationsPage';
import { PermissionsPage } from '@/presentation/pages/PermissionsPage';
import { PermissionsProvider } from '@/presentation/contexts/PermissionsContext';
import { ClinicsPage } from '@/presentation/pages/ClinicsPage';
import { ClinicHubPage } from '@/presentation/pages/ClinicHubPage';
import { ProfessionalsPage } from '@/presentation/pages/ProfessionalsPage';
import { PatientsPage } from '@/presentation/pages/PatientsPage';
import { FinancesPage } from '@/presentation/pages/FinancesPage';
import { ProfessionalDetailPage } from '@/presentation/pages/ProfessionalDetailPage';
import { LoginPage } from '@/presentation/pages/LoginPage';
import { ReportsIndexRedirect } from '@/presentation/pages/ReportsIndexRedirect';
import { CoofitSubscriptionPage } from '@/presentation/pages/CoofitSubscriptionPage';
import { OperationsPage } from '@/presentation/pages/OperationsPage';
import { ComingSoonReportPage } from '@/presentation/pages/ComingSoonReportPage';
import { NotFoundPage } from '@/presentation/pages/NotFoundPage';
import { TeamIndexRedirect, TeamLayout } from '@/presentation/pages/TeamLayout';
import { REPORT_COMING_SOON_PATHS, REPORT_LEGACY_REDIRECTS } from '@/application/reports/reportLegacyRedirects';
import { PreserveSearchRedirect } from '@/presentation/routes/PreserveSearchRedirect';
import { ProtectedRoute } from '@/presentation/routes/ProtectedRoute';
import { HomeRoute } from '@/presentation/routes/HomeRoute';
import { ClinicScopeBlock } from '@/presentation/routes/ClinicScopeBlock';
import { MetricsQueryProvider } from '@/presentation/providers/MetricsQueryProvider';
import { LoadingBlock } from '@/presentation/components/common/LoadingBlock';
import { applyTheme, getInitialTheme } from '@/shared/theme';

/** Reportes y páginas pesadas (charts) se cargan al entrar en la ruta. */
const ClinicEconomicsReportPage = lazy(() =>
  import('@/presentation/pages/reports/ClinicEconomicsReportPage').then((m) => ({
    default: m.ClinicEconomicsReportPage,
  }))
);
const ComparisonReportPage = lazy(() =>
  import('@/presentation/pages/reports/ComparisonReportPage').then((m) => ({
    default: m.ComparisonReportPage,
  }))
);
const MainPrincipalReportPage = lazy(() =>
  import('@/presentation/pages/reports/MainPrincipalReportPage').then((m) => ({
    default: m.MainPrincipalReportPage,
  }))
);
const NoReturnReportPage = lazy(() =>
  import('@/presentation/pages/reports/NoReturnReportPage').then((m) => ({
    default: m.NoReturnReportPage,
  }))
);
const RecurrenceReportPage = lazy(() =>
  import('@/presentation/pages/reports/RecurrenceReportPage').then((m) => ({
    default: m.RecurrenceReportPage,
  }))
);
const AppointmentsReportPage = lazy(() =>
  import('@/presentation/pages/reports/OperationalReportPages').then((m) => ({
    default: m.AppointmentsReportPage,
  }))
);
const CancellationsReportPage = lazy(() =>
  import('@/presentation/pages/reports/OperationalReportPages').then((m) => ({
    default: m.CancellationsReportPage,
  }))
);
const AnnualExecutiveReportPage = lazy(() =>
  import('@/presentation/pages/reports/BoardReportPages').then((m) => ({
    default: m.AnnualExecutiveReportPage,
  }))
);
const ConsolidatedBoardReportPage = lazy(() =>
  import('@/presentation/pages/reports/BoardReportPages').then((m) => ({
    default: m.ConsolidatedBoardReportPage,
  }))
);
const LifetimeValueReportPage = lazy(() =>
  import('@/presentation/pages/reports/GapReportPages').then((m) => ({
    default: m.LifetimeValueReportPage,
  }))
);
const OccupancyReportPage = lazy(() =>
  import('@/presentation/pages/reports/GapReportPages').then((m) => ({
    default: m.OccupancyReportPage,
  }))
);
const ResponsiveDataListDemoPage = import.meta.env.DEV
  ? lazy(() =>
      import('@/presentation/pages/dev/ResponsiveDataListDemoPage').then((m) => ({
        default: m.ResponsiveDataListDemoPage,
      }))
    )
  : null;

function LazyRoute({ children }: { children: ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="panel-page">
          <LoadingBlock minHeightClassName="min-h-[12rem]" />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

function L({ component: Component }: { component: ComponentType }) {
  return (
    <LazyRoute>
      <Component />
    </LazyRoute>
  );
}

export function App() {
  useEffect(() => {
    applyTheme(getInitialTheme());
  }, []);

  return (
    <AuthProvider>
      <MetricsQueryProvider>
        <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
          <PermissionsProvider>
            <ClinicScopeBlock>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route element={<ProtectedRoute />}>
                {/* Soft-redirects legacy: fuera de MainShell para no pelear con GlobalPeriod. */}
                {REPORT_LEGACY_REDIRECTS.map((redirect) => (
                  <Route
                    key={redirect.path}
                    path={redirect.path}
                    element={
                      <PreserveSearchRedirect to={redirect.to} setParams={redirect.setParams} />
                    }
                  />
                ))}
                <Route element={<MainShell />}>
                  <Route index element={<HomeRoute />} />
                  <Route path="clinics" element={<ClinicsPage />} />
                  <Route path="tenants" element={<Navigate to="/clinics" replace />} />
                  <Route path="clinics/:tenantId" element={<ClinicHubPage />} />
                  <Route path="professionals" element={<ProfessionalsPage />} />
                  <Route path="professionals/:profesionalId" element={<ProfessionalDetailPage />} />
                  <Route path="patients" element={<PatientsPage />} />
                  <Route path="finances" element={<FinancesPage />} />
                  <Route path="reports" element={<ReportsIndexRedirect />} />
                  <Route path="reports/main-principal" element={<L component={MainPrincipalReportPage} />} />
                  <Route path="reports/clinic" element={<L component={ClinicEconomicsReportPage} />} />
                  <Route path="reports/comparison" element={<L component={ComparisonReportPage} />} />
                  <Route path="reports/conversion" element={<L component={RecurrenceReportPage} />} />
                  <Route path="reports/no-return" element={<L component={NoReturnReportPage} />} />
                  <Route path="reports/appointments" element={<L component={AppointmentsReportPage} />} />
                  <Route path="reports/cancellations" element={<L component={CancellationsReportPage} />} />
                  <Route path="reports/consolidated" element={<L component={ConsolidatedBoardReportPage} />} />
                  <Route path="reports/annual" element={<L component={AnnualExecutiveReportPage} />} />
                  <Route path="reports/occupancy" element={<L component={OccupancyReportPage} />} />
                  <Route path="reports/lifetime-value" element={<L component={LifetimeValueReportPage} />} />
                  {REPORT_COMING_SOON_PATHS.map((path) => (
                    <Route key={path} path={path} element={<ComingSoonReportPage />} />
                  ))}
                  <Route path="subscription" element={<CoofitSubscriptionPage />} />
                  <Route path="operations" element={<OperationsPage />} />
                  <Route path="team" element={<TeamLayout />}>
                    <Route index element={<TeamIndexRedirect />} />
                    <Route path="assistants" element={<AssistantsPage embedded />} />
                    <Route path="roles" element={<PermissionsPage embedded />} />
                  </Route>
                  <Route path="assistants" element={<Navigate to="/team/assistants" replace />} />
                  <Route path="permissions" element={<Navigate to="/team/roles" replace />} />
                  <Route path="communications" element={<CommunicationsPage />} />
                  <Route path="caja/movimientos" element={<CajaDetailPage />} />
                  <Route path="caja/gestion" element={<Navigate to="/caja/movimientos" replace />} />
                  <Route path="ownership" element={<OwnershipParticipationPage />} />
                  {ResponsiveDataListDemoPage ? (
                    <Route
                      path="dev/responsive-data-list"
                      element={<L component={ResponsiveDataListDemoPage} />}
                    />
                  ) : null}
                  <Route path="*" element={<NotFoundPage />} />
                </Route>
              </Route>
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </ClinicScopeBlock>
          </PermissionsProvider>
        </BrowserRouter>
      </MetricsQueryProvider>
    </AuthProvider>
  );
}
